/**
 * Graph Rendering Utilities
 *
 * Utilities for rendering graph visualizations with custom node/edge rendering,
 * off-screen indicators, and viewport calculations.
 */
export function calculateViewportBounds(centerPos, width, height, zoom) {
    const viewportWidth = width / zoom;
    const viewportHeight = height / zoom;
    return {
        left: centerPos.x - viewportWidth / 2,
        right: centerPos.x + viewportWidth / 2,
        top: centerPos.y - viewportHeight / 2,
        bottom: centerPos.y + viewportHeight / 2,
    };
}
export function isNodeOffScreen(node, viewport, nodeRadius = 5, labelHeight = 25) {
    if (!node.x || !node.y)
        return false;
    return (node.x + nodeRadius < viewport.left ||
        node.x - nodeRadius > viewport.right ||
        node.y - nodeRadius < viewport.top ||
        node.y + nodeRadius + labelHeight > viewport.bottom);
}
export function calculateOffScreenIndicator(node, _viewport, edgeMargin, canvasWidth, canvasHeight, centerPos, zoom) {
    const worldToScreen = (worldX, worldY) => ({
        x: (worldX - centerPos.x) * zoom + canvasWidth / 2,
        y: (worldY - centerPos.y) * zoom + canvasHeight / 2,
    });
    const screenToWorld = (screenX, screenY) => ({
        x: (screenX - canvasWidth / 2) / zoom + centerPos.x,
        y: (screenY - canvasHeight / 2) / zoom + centerPos.y,
    });
    const nodeScreen = worldToScreen(node.x, node.y);
    let indicatorScreenX = nodeScreen.x;
    let indicatorScreenY = nodeScreen.y;
    if (nodeScreen.x < edgeMargin) {
        indicatorScreenX = edgeMargin;
    }
    else if (nodeScreen.x > canvasWidth - edgeMargin) {
        indicatorScreenX = canvasWidth - edgeMargin;
    }
    if (nodeScreen.y < edgeMargin) {
        indicatorScreenY = edgeMargin;
    }
    else if (nodeScreen.y > canvasHeight - edgeMargin) {
        indicatorScreenY = canvasHeight - edgeMargin;
    }
    indicatorScreenX = Math.max(edgeMargin, Math.min(canvasWidth - edgeMargin, indicatorScreenX));
    indicatorScreenY = Math.max(edgeMargin, Math.min(canvasHeight - edgeMargin, indicatorScreenY));
    const indicatorWorld = screenToWorld(indicatorScreenX, indicatorScreenY);
    const dx = node.x - indicatorWorld.x;
    const dy = node.y - indicatorWorld.y;
    const arrowRotation = Math.atan2(dy, dx) + Math.PI;
    return {
        node,
        indicatorX: indicatorWorld.x,
        indicatorY: indicatorWorld.y,
        arrowRotation,
    };
}
export function renderOffScreenIndicator(ctx, offScreenNode, globalScale, isDark) {
    const { node, indicatorX, indicatorY, arrowRotation } = offScreenNode;
    const label = node.label.length > 12 ? node.label.substring(0, 12) + '...' : node.label;
    const fontSize = 11 / globalScale;
    ctx.font = `bold ${fontSize}px Sans-Serif`;
    const textWidth = ctx.measureText(label).width;
    const padding = 6 / globalScale;
    const arrowSize = 6 / globalScale;
    const arrowTextGap = 6 / globalScale;
    const totalWidth = arrowSize + arrowTextGap + textWidth + padding * 2;
    const totalHeight = fontSize + padding * 2;
    ctx.fillStyle = node.color + 'E6';
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = 2 / globalScale;
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 8 / globalScale;
    ctx.shadowOffsetX = 2 / globalScale;
    ctx.shadowOffsetY = 2 / globalScale;
    const rectX = indicatorX - totalWidth / 2;
    const rectY = indicatorY - totalHeight / 2;
    ctx.beginPath();
    ctx.roundRect(rectX, rectY, totalWidth, totalHeight, 6 / globalScale);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    const arrowX = indicatorX - totalWidth / 2 + padding + arrowSize;
    ctx.translate(arrowX, indicatorY);
    ctx.rotate(arrowRotation);
    ctx.fillStyle = isDark ? '#ffffff' : '#ffffff';
    ctx.beginPath();
    ctx.moveTo(-arrowSize, 0);
    ctx.lineTo(0, -arrowSize / 2);
    ctx.lineTo(0, arrowSize / 2);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = isDark ? '#ffffff' : '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const textX = indicatorX - totalWidth / 2 + padding + arrowSize + arrowTextGap;
    ctx.fillText(label, textX, indicatorY);
}
// Icon mappings to Unicode symbols
const ICON_SYMBOLS = {
    brain: '🧠',
    'file-text': '📄',
    square: '🎴',
    zap: '⚡',
    target: '🎯',
    book: '📚',
    layers: '📚',
    'eye-off': '👁️',
    'help-circle': '❓',
    eye: '👁️',
    heart: '❤️',
    users: '👥',
    radio: '📡',
    'book-open': '📖',
    user: '👤',
    'user-circle': '👤',
    calendar: '📅',
    infinity: '∞',
    globe: '🌍',
    'gamepad-2': '🎮',
    'map-pin': '📍',
    'building-2': '🏛️',
    'sliders-horizontal': '⚙️',
    'trending-up': '📈',
    battery: '🔋',
    flame: '🔥',
    coins: '💰',
    cpu: '🖥️',
    'git-branch': '🔀',
    database: '💾',
    network: '🕸️',
};
export function renderNodeWithLabel(node, ctx, globalScale, isDark, isSelected = false, isConnected = false, hasSelectedNode = false) {
    const label = node.label;
    const labelFontSize = isSelected ? 14 / globalScale : 12 / globalScale;
    let nodeSize = (node.size || 5) / globalScale;
    if (isSelected) {
        nodeSize = nodeSize * 1.5;
    }
    else if (isConnected) {
        nodeSize = nodeSize * 1.2;
    }
    let alpha = 1.0;
    if (hasSelectedNode && !isSelected && !isConnected) {
        alpha = 0.25;
    }
    const nodeColor = node.color || '#999';
    ctx.fillStyle = nodeColor + Math.round(alpha * 255).toString(16).padStart(2, '0');
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    ctx.fill();
    if (isSelected) {
        ctx.strokeStyle = isDark ? 'rgba(96, 165, 250, 0.3)' : 'rgba(59, 130, 246, 0.3)';
        ctx.lineWidth = 8 / globalScale;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize + 4 / globalScale, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.strokeStyle = isDark ? 'rgba(96, 165, 250, 1)' : 'rgba(59, 130, 246, 1)';
        ctx.lineWidth = 3 / globalScale;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize + 1 / globalScale, 0, 2 * Math.PI);
        ctx.stroke();
    }
    else if (isConnected) {
        ctx.strokeStyle = isDark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(59, 130, 246, 0.6)';
        ctx.lineWidth = 2 / globalScale;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize + 1 / globalScale, 0, 2 * Math.PI);
        ctx.stroke();
    }
    else if (node.type === 'primitive' || node.primitiveType) {
        ctx.strokeStyle = isDark
            ? `rgba(255, 255, 255, ${alpha * 0.3})`
            : `rgba(0, 0, 0, ${alpha * 0.2})`;
        ctx.lineWidth = 2 / globalScale;
        ctx.stroke();
    }
    const iconStr = node.properties?.icon || node.icon;
    if (iconStr && ICON_SYMBOLS[iconStr]) {
        const iconSize = nodeSize * 0.8;
        ctx.font = `${iconSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.9})`;
        ctx.fillText(ICON_SYMBOLS[iconStr], node.x, node.y);
    }
    const padding = 4 / globalScale;
    const labelY = node.y + nodeSize + labelFontSize + 4 / globalScale;
    ctx.font = isSelected
        ? `bold ${labelFontSize}px Sans-Serif`
        : `600 ${labelFontSize}px Sans-Serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const textWidth = ctx.measureText(label).width;
    const bgHeight = labelFontSize + padding * 2;
    const bgWidth = textWidth + padding * 2;
    if (isSelected) {
        ctx.fillStyle = isDark
            ? `rgba(30, 41, 59, ${alpha * 0.95})`
            : `rgba(255, 255, 255, ${alpha * 0.95})`;
        ctx.strokeStyle = isDark
            ? `rgba(96, 165, 250, ${alpha})`
            : `rgba(59, 130, 246, ${alpha})`;
        ctx.lineWidth = 2 / globalScale;
    }
    else if (isConnected) {
        ctx.fillStyle = isDark
            ? `rgba(30, 41, 59, ${alpha * 0.9})`
            : `rgba(255, 255, 255, ${alpha * 0.9})`;
        ctx.strokeStyle = isDark
            ? `rgba(71, 85, 105, ${alpha * 0.5})`
            : `rgba(203, 213, 225, ${alpha * 0.5})`;
        ctx.lineWidth = 1 / globalScale;
    }
    else {
        ctx.fillStyle = isDark
            ? `rgba(30, 41, 59, ${alpha * 0.85})`
            : `rgba(255, 255, 255, ${alpha * 0.85})`;
        ctx.strokeStyle = 'transparent';
    }
    const rectX = node.x - bgWidth / 2;
    const rectY = labelY - bgHeight / 2;
    const cornerRadius = 3 / globalScale;
    ctx.beginPath();
    ctx.roundRect(rectX, rectY, bgWidth, bgHeight, cornerRadius);
    ctx.fill();
    if (isSelected || isConnected) {
        ctx.stroke();
    }
    const labelColor = isDark ? '#e5e7eb' : '#1f2937';
    const r = parseInt(labelColor.slice(1, 3), 16);
    const g = parseInt(labelColor.slice(3, 5), 16);
    const b = parseInt(labelColor.slice(5, 7), 16);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    ctx.fillText(label, node.x, labelY);
}
export function renderLinkLabel(link, ctx, globalScale, isDark, isSelected = false) {
    const label = link.label || '';
    if (!label)
        return;
    const start = link.source;
    const end = link.target;
    const textPos = {
        x: start.x + (end.x - start.x) / 2,
        y: start.y + (end.y - start.y) / 2,
    };
    const fontSize = isSelected ? 11 / globalScale : 9 / globalScale;
    ctx.font = `${isSelected ? 'bold' : ''} ${fontSize}px Sans-Serif`;
    const textWidth = ctx.measureText(label).width;
    const padding = (isSelected ? 4 : 3) / globalScale;
    if (isSelected) {
        ctx.fillStyle = isDark ? 'rgba(59, 130, 246, 0.95)' : 'rgba(96, 165, 250, 0.95)';
        ctx.strokeStyle = isDark ? 'rgba(96, 165, 250, 1)' : 'rgba(59, 130, 246, 1)';
        ctx.lineWidth = 2 / globalScale;
    }
    else {
        ctx.fillStyle = isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)';
        ctx.strokeStyle = isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(203, 213, 225, 0.5)';
        ctx.lineWidth = 1 / globalScale;
    }
    const rectX = textPos.x - textWidth / 2 - padding;
    const rectY = textPos.y - fontSize / 2 - padding;
    const rectWidth = textWidth + padding * 2;
    const rectHeight = fontSize + padding * 2;
    ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
    ctx.fillStyle = isSelected
        ? isDark
            ? '#ffffff'
            : '#ffffff'
        : isDark
            ? '#94a3b8'
            : '#475569';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, textPos.x, textPos.y);
}
